import { useState, useRef, useEffect, useCallback } from 'react';
import { Mail, ArrowLeft, Loader2, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

interface EmailAuthFlowProps {
  step: 'email-input' | 'otp-verification';
  pendingEmail: string | null;
  isLoading: boolean;
  onRequestOTP: (email: string) => Promise<{ success: boolean; error?: string }>;
  onVerifyOTP: (email: string, code: string) => Promise<{ success: boolean; error?: string }>;
  onBack: () => void;
}

export function EmailAuthFlow({
  step,
  pendingEmail,
  isLoading,
  onRequestOTP,
  onVerifyOTP,
  onBack,
}: EmailAuthFlowProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div
            className={`w-2.5 h-2.5 rounded-full transition-colors ${
              step === 'email-input' ? 'bg-purple-600' : 'bg-purple-300'
            }`}
          />
          <div
            className={`w-2.5 h-2.5 rounded-full transition-colors ${
              step === 'otp-verification' ? 'bg-purple-600' : 'bg-gray-300'
            }`}
          />
        </div>

        {step === 'email-input' ? (
          <EmailInputStep
            isLoading={isLoading}
            onSubmit={onRequestOTP}
            onBack={onBack}
          />
        ) : (
          <OTPVerificationStep
            email={pendingEmail || ''}
            isLoading={isLoading}
            onVerify={onVerifyOTP}
            onResend={onRequestOTP}
            onBack={onBack}
          />
        )}
      </div>
    </div>
  );
}

interface EmailInputStepProps {
  isLoading: boolean;
  onSubmit: (email: string) => Promise<{ success: boolean; error?: string }>;
  onBack: () => void;
}

function EmailInputStep({ isLoading, onSubmit, onBack }: EmailInputStepProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    const result = await onSubmit(email);
    if (!result.success) {
      setError(result.error || 'Failed to send verification code');
    }
  };

  return (
    <>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-4">
          <Mail className="w-8 h-8 text-purple-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Enter your email</h1>
        <p className="text-gray-600">
          We'll send you a verification code to sign in
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="sr-only">
            Email address
          </label>
          <input
            ref={inputRef}
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
            placeholder="name@company.com"
            disabled={isLoading}
            data-testid="email-auth-input-email"
            className={`w-full px-4 py-3 text-lg border-2 rounded-xl focus:outline-none focus:ring-0 transition-colors ${
              error
                ? 'border-red-300 focus:border-red-500'
                : 'border-gray-200 focus:border-purple-500'
            } disabled:bg-gray-50 disabled:cursor-not-allowed`}
            autoComplete="email"
          />
          {error && (
            <div data-testid="email-auth-error" className="flex items-center gap-2 mt-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading || !email.trim()}
          data-testid="email-auth-btn-continue"
          className="w-full py-3 px-4 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Sending code...
            </>
          ) : (
            'Continue'
          )}
        </button>
      </form>

      {/* Back Link */}
      <button
        onClick={onBack}
        disabled={isLoading}
        data-testid="email-auth-btn-back"
        className="flex items-center gap-2 mx-auto mt-6 text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to login options
      </button>
    </>
  );
}

interface OTPVerificationStepProps {
  email: string;
  isLoading: boolean;
  onVerify: (email: string, code: string) => Promise<{ success: boolean; error?: string }>;
  onResend: (email: string) => Promise<{ success: boolean; error?: string }>;
  onBack: () => void;
}

function OTPVerificationStep({
  email,
  isLoading,
  onVerify,
  onResend,
  onBack,
}: OTPVerificationStepProps) {
  const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(600); // 10 minutes in seconds
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Countdown timer for OTP expiration
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const maskedEmail = email.replace(/(.{2})(.*)(@.*)/, '$1***$3');

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);
      setError(null);
      // Focus the next empty input or the last one
      const nextEmptyIndex = newOtp.findIndex((val) => !val);
      inputRefs.current[nextEmptyIndex === -1 ? 5 : nextEmptyIndex]?.focus();
    }
  };

  const handleSubmit = useCallback(async () => {
    const code = otp.join('');
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    const result = await onVerify(email, code);
    if (!result.success) {
      setError(result.error || 'Invalid verification code');
      setOtp(Array(6).fill(''));
      inputRefs.current[0]?.focus();
    }
  }, [otp, email, onVerify]);

  // Auto-submit when all digits are entered
  useEffect(() => {
    const code = otp.join('');
    if (code.length === 6 && !isLoading) {
      handleSubmit();
    }
  }, [otp, isLoading, handleSubmit]);

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setError(null);
    setResendSuccess(false);
    const result = await onResend(email);
    if (result.success) {
      setResendSuccess(true);
      setResendCooldown(30); // 30 second cooldown
      setCountdown(600); // Reset countdown
      setTimeout(() => setResendSuccess(false), 3000);
    } else {
      setError(result.error || 'Failed to resend code');
    }
  };

  return (
    <>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-4">
          <Mail className="w-8 h-8 text-purple-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h1>
        <p className="text-gray-600">
          We sent a code to <span className="font-medium text-gray-900">{maskedEmail}</span>
        </p>
      </div>

      {/* OTP Input */}
      <div className="space-y-4">
        <div className="flex justify-center gap-2 sm:gap-3">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              disabled={isLoading}
              data-testid={`email-auth-input-otp-${index}`}
              className={`w-11 h-14 sm:w-12 sm:h-16 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none focus:ring-0 transition-colors ${
                error
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-200 focus:border-purple-500'
              } disabled:bg-gray-50 disabled:cursor-not-allowed`}
              aria-label={`Digit ${index + 1}`}
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-center justify-center gap-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Success Message */}
        {resendSuccess && (
          <div className="flex items-center justify-center gap-2 text-green-600 text-sm">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            <span>New code sent!</span>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center gap-2 text-purple-600">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Verifying...</span>
          </div>
        )}

        {/* Timer and Resend */}
        <div className="text-center text-sm">
          {countdown > 0 ? (
            <p className="text-gray-500">
              Code expires in <span className="font-medium">{formatTime(countdown)}</span>
            </p>
          ) : (
            <p className="text-red-600 font-medium">Code expired</p>
          )}
        </div>

        <div className="text-center">
          <button
            onClick={handleResend}
            disabled={isLoading || resendCooldown > 0}
            data-testid="email-auth-btn-resend"
            className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            {resendCooldown > 0
              ? `Resend in ${resendCooldown}s`
              : "Didn't receive code? Resend"}
          </button>
        </div>
      </div>

      {/* Back Link */}
      <button
        onClick={onBack}
        disabled={isLoading}
        data-testid="email-auth-btn-back-otp"
        className="flex items-center gap-2 mx-auto mt-8 text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
      >
        <ArrowLeft className="w-4 h-4" />
        Use different email
      </button>
    </>
  );
}
